import { memo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardHeader,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Tooltip,
  Chip,
  InputAdornment,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
} from "@mui/material";
import axios from "../customAxios.js";
import { getPublicS3Url } from "../utils/s3Utils";
import { useAuth } from "../hooks/useAuth.jsx";
import { activeStatusToEditItems } from "../helpers/enums.js";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NormalDeliveryItemTable from "./tables/NormalDeliveryItemTable.jsx";
import DriverDeliveryItemTable from "./tables/DriverDeliveryItemTable.jsx";

const DeliveryItems = ({
  delivery,
  deliveryItems,
  deliveryItemsCount,
  pagination,
  handleChangePage,
  handleChangeRowsPerPage,
  createDeliveryItem,
  deleteDeliveryItem,
  updateDeliveryItemStatus,
  updateDeliveryItemLogistics,
  setItemsCount,
  onDispatchPrepared,
  loading,
  error,
  onItemsUpdated,
  orderProducts = [],
}) => {
  const { user } = useAuth();
  const planAddons =
    user?.admin_plan?.admin_permissions?.addons ||
    user?.seller_admin_plan?.admin_permissions?.addons ||
    {};
  const hasFulfillmentPro = planAddons.fulfillment_pro === true;

  const [externalProviders] = useState([
    "fedex",
    "bluex",
    "startken",
    "uberdirect",
    "shippify",
    "packet",
  ]);
  const [selectedItemStatus, setSelectedItemStatus] = useState(null);
  const [editStatus, setEditStatus] = useState({ id: null, state: false });
  const [isProcessingItem, setIsProcessingItem] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // set count dialog state
  const [openSetCountDialog, setOpenSetCountDialog] = useState(false);
  const [newCountValue, setNewCountValue] = useState("");
  const [settingCount, setSettingCount] = useState(false);

  // logistics variables state
  const [editingLogisticsItem, setEditingLogisticsItem] = useState(null);
  const [logisticsForm, setLogisticsForm] = useState({
    weight_grams: "",
    height_cm: "",
    width_cm: "",
    length_cm: "",
  });
  const [logisticsProductQtys, setLogisticsProductQtys] = useState({});
  const [savingLogistics, setSavingLogistics] = useState(false);
  const [dispatching, setDispatching] = useState(false);

  const handleCloseAlert = (event, reason) => {
    if (reason === "clickaway") return;
    setAlertInfo({ ...alertInfo, open: false });
  };

  const handleShowAlert = (message, severity = "success") => {
    setAlertInfo({ open: true, message, severity });
  };

  const handleAddDeliveryItem = async () => {
    setIsProcessingItem(true);
    try {
      await createDeliveryItem();
      handleShowAlert("Bulto agregado. Generando etiquetas...");
      if (onItemsUpdated) {
        setTimeout(() => {
          onItemsUpdated();
        }, 1000);
      }
    } catch (error) {
      console.error("Error al agregar bulto:", error);
      const errorMsg =
        error.response?.data?.error || "Ocurrió un error al agregar el bulto.";
      handleShowAlert(errorMsg, "error");
    } finally {
      setIsProcessingItem(false);
    }
  };

  const handleDeleteDeliveryItem = async (itemId) => {
    if (
      !window.confirm(
        "¿Estás seguro de que deseas eliminar este bulto? Se regenerarán las etiquetas y se re-indexarán los bultos.",
      )
    )
      return;

    setIsProcessingItem(true);
    try {
      await deleteDeliveryItem(itemId);
      handleShowAlert("Bulto eliminado y re-indexado correctamente.");
      if (onItemsUpdated) {
        setTimeout(() => {
          onItemsUpdated();
        }, 1000);
      }
    } catch (error) {
      console.error("Error al eliminar bulto:", error);
      const errorMsg =
        error.response?.data?.error || "Ocurrió un error al eliminar el bulto.";
      handleShowAlert(errorMsg, "error");
    } finally {
      setIsProcessingItem(false);
    }
  };

  const handleOpenPdf = (labelUrl) => {
    if (!labelUrl) return;
    try {
      const url = new URL(labelUrl);
      const s3Key = url.pathname.substring(1);
      const publicUrl = getPublicS3Url(
        process.env.REACT_APP_S3_BUCKET_NAME,
        s3Key,
      );
      window.open(publicUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      window.open(labelUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleItemStatusUpdate = async (item, status) => {
    setIsProcessingItem(true);
    try {
      await updateDeliveryItemStatus(item.id, status, delivery.order_id);
      handleShowAlert("Bulto actualizado...");
      if (onItemsUpdated) {
        setTimeout(() => {
          onItemsUpdated();
        }, 1000);
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.error ||
        "Ocurrió un error al actualizar el bulto.";
      handleShowAlert(errorMsg, "error");
    } finally {
      setIsProcessingItem(false);
      setEditStatus({ id: null, state: false });
    }
  };

  const isWaitingForAssignment =
    delivery?.status === "waiting_for_assignment" ||
    delivery?.status === "fulfilled" ||
    delivery?.status === "packed";

  const itemHasLogistics = (item) =>
    item.weight_grams > 0 &&
    item.height_cm > 0 &&
    item.width_cm > 0 &&
    item.length_cm > 0;

  const allItemsPrepared =
    deliveryItems.length > 0 && deliveryItems.every(itemHasLogistics);

  const totalWeightGrams = deliveryItems.reduce(
    (sum, item) => sum + (item.weight_grams || 0),
    0,
  );
  const totalVolumeCm3 = deliveryItems.reduce(
    (sum, item) =>
      sum +
      (item.height_cm || 0) * (item.width_cm || 0) * (item.length_cm || 0),
    0,
  );
  const itemsWithLogistics = deliveryItems.filter(itemHasLogistics).length;

  const handleSetCount = async () => {
    const count = parseInt(newCountValue, 10);
    if (!count || count < 1) return;
    setSettingCount(true);
    try {
      await setItemsCount(count);
      handleShowAlert(`Cantidad de bultos establecida a ${count}`);
      setOpenSetCountDialog(false);
      if (onItemsUpdated) onItemsUpdated();
    } catch (err) {
      const msg = err.response?.data?.error || "Error al establecer la cantidad";
      handleShowAlert(msg, "error");
    } finally {
      setSettingCount(false);
    }
  };

  const getAvailableQty = (op) => {
    const assignedElsewhere = deliveryItems
      .filter((item) => item.id !== editingLogisticsItem?.id)
      .reduce((sum, item) => {
        const assignment = (item.item_products || []).find(
          (ip) => ip.order_product === op.id,
        );
        return sum + (assignment?.prepared_quantity || 0);
      }, 0);
    return op.quantity - assignedElsewhere;
  };

  const openLogisticsDialog = (item) => {
    setEditingLogisticsItem(item);
    setLogisticsForm({
      weight_grams: item.weight_grams || "",
      height_cm: item.height_cm || "",
      width_cm: item.width_cm || "",
      length_cm: item.length_cm || "",
    });
    const initialQtys = {};
    (item.item_products || []).forEach((ip) => {
      initialQtys[ip.order_product] = String(ip.prepared_quantity);
    });
    setLogisticsProductQtys(initialQtys);
  };

  const handleSaveLogistics = async () => {
    if (!editingLogisticsItem || !updateDeliveryItemLogistics) return;
    setSavingLogistics(true);
    try {
      const products = orderProducts.map((op) => ({
        order_product_id: op.id,
        prepared_quantity: parseInt(logisticsProductQtys[op.id] || "0", 10),
      }));
      const updatedItem = await updateDeliveryItemLogistics(editingLogisticsItem.id, {
        weight_grams: logisticsForm.weight_grams
          ? parseInt(logisticsForm.weight_grams, 10)
          : null,
        height_cm: logisticsForm.height_cm
          ? parseInt(logisticsForm.height_cm, 10)
          : null,
        width_cm: logisticsForm.width_cm
          ? parseInt(logisticsForm.width_cm, 10)
          : null,
        length_cm: logisticsForm.length_cm
          ? parseInt(logisticsForm.length_cm, 10)
          : null,
        products,
      });
      handleShowAlert("Variables logísticas guardadas");
      setEditingLogisticsItem(null);

      // Auto-update delivery.weight when all items are ready
      const updatedItems = deliveryItems.map((item) =>
        item.id === editingLogisticsItem.id ? updatedItem : item,
      );
      if (updatedItems.every(itemHasLogistics) && delivery?.id) {
        const totalGrams = updatedItems.reduce(
          (sum, item) => sum + (item.weight_grams || 0),
          0,
        );
        await axios.patch(`deliveries/${delivery.id}/update_weight/`, {
          weight: totalGrams,
        });
      }
    } catch (err) {
      const msg =
        err.response?.data?.error || "Error al guardar las variables";
      handleShowAlert(msg, "error");
    } finally {
      setSavingLogistics(false);
    }
  };

  const handleDispatch = async () => {
    if (!onDispatchPrepared) return;
    setDispatching(true);
    try {
      await onDispatchPrepared();
      handleShowAlert(
        "Pedido enviado al courier. Se está procesando...",
        "success",
      );
    } catch (err) {
      const msg =
        err.response?.data?.error || "Error al enviar el pedido al courier";
      handleShowAlert(msg, "error");
    } finally {
      setDispatching(false);
    }
  };

  const activeStatusToEdit = () =>
    !activeStatusToEditItems.includes(delivery.status);

  const getButtonLabelText = () => {
    if (
      externalProviders.includes(delivery.shipping_zone?.courier?.toLowerCase())
    ) {
      return "Ver todas las etiquetas";
    }

    return "Ver Etiqueta";
  };

  const getEmptyLabelText = () => {
    if (
      externalProviders.includes(delivery.shipping_zone?.courier?.toLowerCase())
    ) {
      return "-";
    }

    return "Procesando etiqueta...";
  };

  if (!hasFulfillmentPro) {
    return (
      <Box sx={{ width: "100%", pt: "16px" }}>
        <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid item xs={12}>
            <Card variant="outlined" style={{ marginBottom: "20px" }}>
              <CardHeader
                title={
                  <Typography variant="h6" fontWeight="bold">
                    Detalle de Bultos
                  </Typography>
                }
                action={
                  !user?.user_type?.includes("driver") && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={
                        isProcessingItem ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <AddIcon />
                        )
                      }
                      onClick={handleAddDeliveryItem}
                      disabled={isProcessingItem || loading || activeStatusToEdit()}
                      sx={{ textTransform: "none", fontWeight: "bold" }}
                    >
                      Añadir Bulto
                    </Button>
                  )
                }
                sx={{ padding: "10px 16px" }}
              />

              {error && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Alert severity="error">{error}</Alert>
                </Box>
              )}

              {user?.user_type?.includes("driver") ? (
                <DriverDeliveryItemTable
                  delivery={delivery}
                  deliveryItems={deliveryItems}
                  deliveryItemsCount={deliveryItemsCount}
                  pagination={pagination}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  handleItemStatusUpdate={handleItemStatusUpdate}
                  activeStatusToEdit={activeStatusToEdit}
                  getButtonLabelText={getButtonLabelText}
                  getEmptyLabelText={getEmptyLabelText}
                  editStatus={editStatus}
                  setEditStatus={setEditStatus}
                  selectedItemStatus={selectedItemStatus}
                  setSelectedItemStatus={setSelectedItemStatus}
                  isProcessingItem={isProcessingItem}
                  handleOpenPdf={handleOpenPdf}
                  loading={loading}
                />
              ) : (
                <NormalDeliveryItemTable
                  delivery={delivery}
                  deliveryItems={deliveryItems}
                  deliveryItemsCount={deliveryItemsCount}
                  pagination={pagination}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  handleItemStatusUpdate={handleItemStatusUpdate}
                  handleDeleteDeliveryItem={handleDeleteDeliveryItem}
                  activeStatusToEdit={activeStatusToEdit}
                  getButtonLabelText={getButtonLabelText}
                  getEmptyLabelText={getEmptyLabelText}
                  editStatus={editStatus}
                  setEditStatus={setEditStatus}
                  selectedItemStatus={selectedItemStatus}
                  setSelectedItemStatus={setSelectedItemStatus}
                  isProcessingItem={isProcessingItem}
                  handleOpenPdf={handleOpenPdf}
                  loading={loading}
                />
              )}
            </Card>
          </Grid>
        </Grid>

        <Snackbar
          open={alertInfo.open}
          autoHideDuration={6000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseAlert}
            severity={alertInfo.severity}
            sx={{ width: "100%" }}
          >
            {alertInfo.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", pt: "16px" }}>
      <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid item xs={12}>
          <Card variant="outlined" style={{ marginBottom: "20px" }}>
            <CardHeader
              title={
                <Typography variant="h6" fontWeight="bold">
                  Detalle de Bultos 2
                </Typography>
              }
              action={
                !user?.user_type?.includes("driver") && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    {isWaitingForAssignment && (
                      <Tooltip
                        title={
                          allItemsPrepared
                            ? "Enviar al courier"
                            : "Completa las variables logísticas de todos los bultos primero"
                        }
                      >
                        <span>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={
                              dispatching ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <LocalShippingIcon />
                              )
                            }
                            onClick={handleDispatch}
                            disabled={!allItemsPrepared || dispatching}
                            sx={{ textTransform: "none", fontWeight: "bold" }}
                          >
                            Enviar al courier
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={
                        isProcessingItem ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <AddIcon />
                        )
                      }
                      onClick={handleAddDeliveryItem}
                      disabled={
                        isProcessingItem || loading || activeStatusToEdit()
                      }
                      sx={{ textTransform: "none", fontWeight: "bold" }}
                    >
                      Añadir Bulto
                    </Button>
                    <Tooltip title="Cambiar cantidad total de bultos">
                      <Chip
                        label={`${deliveryItemsCount} bultos`}
                        icon={<EditIcon fontSize="small" />}
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setNewCountValue(String(deliveryItemsCount));
                          setOpenSetCountDialog(true);
                        }}
                        disabled={activeStatusToEdit()}
                        sx={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </Stack>
                )
              }
              sx={{
                padding: "10px 16px",
              }}
            />

            {deliveryItems.length > 0 && (
              <>
                {/* KPI strip */}
                <Box
                  sx={{
                    display: "flex",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "action.hover",
                  }}
                >
                  {[
                    {
                      label: "Peso total",
                      value: totalWeightGrams > 0
                        ? `${totalWeightGrams.toLocaleString("es-CL")} g · ${(totalWeightGrams / 1000).toFixed(2)} kg`
                        : "—",
                    },
                    {
                      label: "Volumen total",
                      value: totalVolumeCm3 > 0
                        ? `${totalVolumeCm3.toLocaleString("es-CL")} cm³`
                        : "—",
                    },
                    {
                      label: `Logística completa`,
                      value: `${itemsWithLogistics} / ${deliveryItems.length} bultos`,
                      progress: deliveryItems.length > 0 ? (itemsWithLogistics / deliveryItems.length) * 100 : 0,
                    },
                  ].map((kpi, i, arr) => (
                    <Box
                      key={i}
                      sx={{
                        flex: 1,
                        px: 2,
                        py: 1,
                        borderRight: i < arr.length - 1 ? "1px solid" : "none",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" display="block">
                        {kpi.label}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {kpi.value}
                      </Typography>
                      {kpi.progress !== undefined && (
                        <LinearProgress
                          variant="determinate"
                          value={kpi.progress}
                          sx={{ mt: 0.5, height: 3, borderRadius: 2 }}
                          color={kpi.progress === 100 ? "success" : "primary"}
                        />
                      )}
                    </Box>
                  ))}
                </Box>

                {/* Per-bulto summary table */}
                <Box
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    overflowX: "auto",
                  }}
                >
                  <Table size="small" sx={{ minWidth: 480 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ py: 0.75, fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", width: 60 }}>
                          Bulto
                        </TableCell>
                        <TableCell sx={{ py: 0.75, fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary" }}>
                          Productos
                        </TableCell>
                        <TableCell sx={{ py: 0.75, fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", width: 110 }}>
                          Peso
                        </TableCell>
                        <TableCell sx={{ py: 0.75, fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", width: 130 }}>
                          Dimensiones
                        </TableCell>
                        <TableCell sx={{ py: 0.75, fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", width: 90, textAlign: "center" }}>
                          Estado
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deliveryItems.map((item) => {
                        const hasLogistics = itemHasLogistics(item);
                        const productos = (item.item_products || [])
                          .map((ip) => {
                            const op = orderProducts.find((p) => p.id === ip.order_product);
                            return op ? `${op.product.name} ×${ip.prepared_quantity}` : null;
                          })
                          .filter(Boolean);

                        return (
                          <TableRow
                            key={item.id}
                            sx={{ "&:last-child td": { borderBottom: 0 } }}
                          >
                            <TableCell sx={{ py: 0.75 }}>
                              <Typography variant="body2" fontWeight="bold">
                                #{item.item_index}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              {productos.length > 0 ? (
                                <Stack spacing={0.25}>
                                  {productos.map((p, i) => (
                                    <Typography key={i} variant="caption" color="text.secondary">
                                      {p}
                                    </Typography>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.disabled">
                                  Sin asignar
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <Typography variant="caption" color={item.weight_grams ? "text.primary" : "text.disabled"}>
                                {item.weight_grams
                                  ? `${item.weight_grams.toLocaleString("es-CL")} g`
                                  : "—"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <Typography variant="caption" color={hasLogistics ? "text.primary" : "text.disabled"}>
                                {item.height_cm && item.width_cm && item.length_cm
                                  ? `${item.height_cm}×${item.width_cm}×${item.length_cm} cm`
                                  : "—"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 0.75, textAlign: "center" }}>
                              <Chip
                                label={hasLogistics ? "Listo" : "Pendiente"}
                                size="small"
                                color={hasLogistics ? "success" : "default"}
                                sx={{ fontSize: "0.7rem", height: 20 }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}

            {error && (
              <Box sx={{ px: 2, pb: 2 }}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}

            {user?.user_type?.includes("driver") ? (
              <DriverDeliveryItemTable
                delivery={delivery}
                deliveryItems={deliveryItems}
                deliveryItemsCount={deliveryItemsCount}
                pagination={pagination}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handleItemStatusUpdate={handleItemStatusUpdate}
                activeStatusToEdit={activeStatusToEdit}
                getButtonLabelText={getButtonLabelText}
                getEmptyLabelText={getEmptyLabelText}
                editStatus={editStatus}
                setEditStatus={setEditStatus}
                selectedItemStatus={selectedItemStatus}
                setSelectedItemStatus={setSelectedItemStatus}
                isProcessingItem={isProcessingItem}
                handleOpenPdf={handleOpenPdf}
                loading={loading}
              />
            ) : (
              <NormalDeliveryItemTable
                delivery={delivery}
                deliveryItems={deliveryItems}
                deliveryItemsCount={deliveryItemsCount}
                pagination={pagination}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handleItemStatusUpdate={handleItemStatusUpdate}
                handleDeleteDeliveryItem={handleDeleteDeliveryItem}
                activeStatusToEdit={activeStatusToEdit}
                getButtonLabelText={getButtonLabelText}
                getEmptyLabelText={getEmptyLabelText}
                editStatus={editStatus}
                setEditStatus={setEditStatus}
                selectedItemStatus={selectedItemStatus}
                setSelectedItemStatus={setSelectedItemStatus}
                isProcessingItem={isProcessingItem}
                handleOpenPdf={handleOpenPdf}
                loading={loading}
                isWaitingForAssignment={isWaitingForAssignment}
                itemHasLogistics={itemHasLogistics}
                openLogisticsDialog={openLogisticsDialog}
              />
            )}
          </Card>
        </Grid>

      </Grid>

      {/* Dialog for setting total bultos count */}
      <Dialog
        open={openSetCountDialog}
        onClose={() => setOpenSetCountDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Establecer cantidad de bultos</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total actual: <strong>{deliveryItemsCount}</strong> bulto
              {deliveryItemsCount !== 1 ? "s" : ""}. Los bultos extras se
              agregarán o eliminarán desde el final.
            </Typography>
            <TextField
              label="Nueva cantidad total"
              type="number"
              size="small"
              fullWidth
              autoFocus
              value={newCountValue}
              onChange={(e) => setNewCountValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSetCount()}
              inputProps={{ min: 1, max: 50 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">bultos</InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSetCountDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSetCount}
            disabled={
              settingCount ||
              !newCountValue ||
              parseInt(newCountValue, 10) < 1 ||
              parseInt(newCountValue, 10) === deliveryItemsCount
            }
          >
            {settingCount ? <CircularProgress size={20} /> : "Establecer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for editing logistics variables */}
      <Dialog
        open={!!editingLogisticsItem}
        onClose={() => setEditingLogisticsItem(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Variables logísticas
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Bulto {editingLogisticsItem?.item_index} de {editingLogisticsItem?.total_items}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>

            {/* Products — compact row per product */}
            {orderProducts.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, display: "block", mb: 0.75 }}
                >
                  Productos del bulto
                </Typography>
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                  {orderProducts.map((op, idx) => {
                    const available = getAvailableQty(op);
                    const currentVal = logisticsProductQtys[op.id] ?? "";
                    const isOver = parseInt(currentVal || "0", 10) > available;
                    return (
                      <Box
                        key={op.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1.5,
                          py: 0.75,
                          borderBottom: idx < orderProducts.length - 1 ? "1px solid" : "none",
                          borderColor: "divider",
                          bgcolor: isOver ? "error.50" : "transparent",
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {op.product.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {available} disp. / {op.quantity} total
                          </Typography>
                        </Box>
                        <TextField
                          type="number"
                          size="small"
                          placeholder="0"
                          value={currentVal}
                          onChange={(e) =>
                            setLogisticsProductQtys((prev) => ({
                              ...prev,
                              [op.id]: e.target.value,
                            }))
                          }
                          inputProps={{ min: 0, max: available, style: { textAlign: "center", padding: "4px 6px" } }}
                          error={isOver}
                          sx={{ width: 84 }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Dimensions — 2×2 grid */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, display: "block", mb: 0.75 }}
              >
                Medidas y peso
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                <TextField
                  label="Peso (g)"
                  type="number"
                  size="small"
                  fullWidth
                  value={logisticsForm.weight_grams}
                  onChange={(e) => setLogisticsForm((p) => ({ ...p, weight_grams: e.target.value }))}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  label="Alto (cm)"
                  type="number"
                  size="small"
                  fullWidth
                  value={logisticsForm.height_cm}
                  onChange={(e) => setLogisticsForm((p) => ({ ...p, height_cm: e.target.value }))}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  label="Ancho (cm)"
                  type="number"
                  size="small"
                  fullWidth
                  value={logisticsForm.width_cm}
                  onChange={(e) => setLogisticsForm((p) => ({ ...p, width_cm: e.target.value }))}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  label="Largo (cm)"
                  type="number"
                  size="small"
                  fullWidth
                  value={logisticsForm.length_cm}
                  onChange={(e) => setLogisticsForm((p) => ({ ...p, length_cm: e.target.value }))}
                  inputProps={{ min: 1 }}
                />
              </Box>
            </Box>

          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditingLogisticsItem(null)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveLogistics}
            disabled={savingLogistics}
          >
            {savingLogistics ? <CircularProgress size={20} /> : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alertInfo.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertInfo.severity}
          sx={{ width: "100%" }}
        >
          {alertInfo.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeliveryItems;
